/* global setTimeout, clearTimeout */
import { Button, makeStyles, Input, tokens } from "@fluentui/react-components";
import { Search20Regular, Dismiss20Regular } from "@fluentui/react-icons";
import * as React from "react";
import { useAtomValue } from "jotai";
import { activeTabAtom } from "@/state";

interface TabHeaderProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
}

export default function TabHeader({ searchQuery, onSearchChange }: TabHeaderProps) {
  const styles = useStyles();
  const activeTab = useAtomValue(activeTabAtom);
  const [localSearchQuery, setLocalSearchQuery] = React.useState<string>(searchQuery);
  const timerRef = React.useRef<any>(null);

  // Debounce search query - only update parent when user stops typing for 300ms
  React.useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      onSearchChange(localSearchQuery);
    }, 300);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [localSearchQuery, onSearchChange]);

  if (activeTab !== "home") {
    return null;
  }

  return (
    <div className={styles.container}>
      <Input
        appearance="outline"
        className={styles.searchInput}
        placeholder="Search questions..."
        value={localSearchQuery}
        onChange={(_, data) => setLocalSearchQuery(data.value)}
        contentBefore={<Search20Regular />}
        contentAfter={
          searchQuery && (
            <Button
              appearance="transparent"
              icon={<Dismiss20Regular />}
              onClick={() => {
                setLocalSearchQuery("");
                onSearchChange("");
              }}
              size="small"
              className={styles.clearButton}
            />
          )
        }
      />
    </div>
  );
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    paddingLeft: "12px",
    paddingRight: "12px",
    paddingTop: "8px",
    paddingBottom: "8px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':hover': {
      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
  },
  clearButton: {
    marginRight: "4px",
  },
});
